// Re-exports: reuse existing Admin.js logic for tournaments, leaderboard, channels, users.
// We extract the inner sub-components from the previous monolithic Admin page and expose them as separate sections.
import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Plus, Trash2, Edit3, Save } from "lucide-react";

export function AdminTournaments() {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const empty = {
    name: "", game: "EA FC 26", format: "Single Elimination",
    start_time: new Date(Date.now() + 86400000 * 3).toISOString().slice(0, 16),
    location: "Online", max_players: 16, prize: "", description: "",
    status: "upcoming", image_url: "",
  };
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");

  const load = async () => { const { data } = await api.get("/tournaments"); setItems(data); };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = { ...form, start_time: new Date(form.start_time).toISOString() };
      if (editingId) await api.put(`/tournaments/${editingId}`, body);
      else await api.post("/tournaments", body);
      setForm(empty); setEditingId(null); setCreating(false); load();
    } catch (e) { setError(formatApiError(e)); }
  };

  const edit = (t) => {
    setEditingId(t.id);
    setCreating(true);
    setForm({
      ...t, start_time: new Date(t.start_time).toISOString().slice(0, 16),
      prize: t.prize || "", description: t.description || "", image_url: t.image_url || "",
    });
  };

  const remove = async (id) => {
    if (!window.confirm("Löschen?")) return;
    await api.delete(`/tournaments/${id}`); load();
  };

  return (
    <div className="space-y-6 fade-in" data-testid="admin-tournaments-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gradient">Turniere</div>
          <h1 className="font-display text-3xl tracking-tight mt-2">Turnier-Verwaltung</h1>
        </div>
        <button onClick={() => { setEditingId(null); setForm(empty); setCreating(true); }} className="btn-primary" data-testid="create-tournament-btn">
          <Plus className="w-4 h-4" /> Neues Turnier
        </button>
      </div>

      {error && <div className="text-red-400 text-sm font-mono">{error}</div>}

      {creating && (
        <form onSubmit={submit} className="card-solid p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2 font-mono text-[10px] uppercase tracking-[0.3em] text-gradient">{editingId ? "Bearbeiten" : "Neu"}</div>
          {[
            ["name", "Name", "input"], ["game", "Game", "input"], ["start_time", "Start", "datetime-local"],
            ["format", "Format", "input"], ["location", "Location", "input"], ["prize", "Prize", "input"],
          ].map(([k, l, t]) => (
            <div key={k}>
              <label className="label">{l}</label>
              <input type={t === "datetime-local" ? "datetime-local" : "text"} required={k === "name" || k === "start_time"} className="input" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
            </div>
          ))}
          <div>
            <label className="label">Max Players</label>
            <input type="number" className="input" min={2} value={form.max_players} onChange={(e) => setForm({ ...form, max_players: parseInt(e.target.value || "0", 10) })} />
          </div>
          <div>
            <label className="label">Status</label>
            <select className="input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="upcoming">upcoming</option>
              <option value="live">live</option>
              <option value="completed">completed</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="label">Image URL</label>
            <input className="input" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} />
          </div>
          <div className="md:col-span-2">
            <label className="label">Beschreibung</label>
            <textarea className="input" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="md:col-span-2 flex gap-2">
            <button type="submit" className="btn-primary"><Save className="w-4 h-4" /> Speichern</button>
            <button type="button" onClick={() => { setCreating(false); setEditingId(null); }} className="btn-secondary">Abbrechen</button>
          </div>
        </form>
      )}

      <div className="space-y-2">
        {items.map((t) => (
          <div key={t.id} className="card-solid p-4 flex items-center gap-3" data-testid={`admin-tournament-${t.id}`}>
            <span className={`chip ${t.status === "live" ? "border-red-400/40 text-red-400" : t.status === "completed" ? "border-accent/40 text-accent" : "border-white/20 text-white/60"}`}>{t.status}</span>
            <div className="flex-1 min-w-0">
              <div className="font-display text-base truncate">{t.name}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">{t.game} · {new Date(t.start_time).toLocaleString("de-DE")}</div>
            </div>
            <span className="font-mono tabular-nums text-sm">{t.participants?.length || 0}/{t.max_players}</span>
            <button onClick={() => edit(t)} className="btn-ghost !p-2"><Edit3 className="w-4 h-4" /></button>
            <button onClick={() => remove(t.id)} className="btn-ghost !p-2 text-red-400"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdminLeaderboardClassic() {
  const [users, setUsers] = useState([]);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ user_id: "", game: "EA FC 26", points: 0, wins: 0, losses: 0, draws: 0 });

  const load = async () => {
    const [u, l] = await Promise.all([api.get("/users"), api.get("/leaderboard", { params: { limit: 100 } })]);
    setUsers(u.data); setItems(l.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    await api.post("/leaderboard", form);
    setForm({ user_id: "", game: "EA FC 26", points: 0, wins: 0, losses: 0, draws: 0 });
    load();
  };

  const remove = async (uid, game) => {
    await api.delete(`/leaderboard/${uid}/${encodeURIComponent(game)}`);
    load();
  };

  return (
    <div className="space-y-6 fade-in" data-testid="admin-leaderboard-page">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gradient">Klassische Rangliste</div>
        <h1 className="font-display text-3xl tracking-tight mt-2">EA FC 26 · Punktetabelle</h1>
      </div>
      <form onSubmit={submit} className="card-solid p-5 grid grid-cols-2 md:grid-cols-6 gap-3">
        <div className="col-span-2">
          <label className="label">Member</label>
          <select required className="input" value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })}>
            <option value="">— wählen —</option>
            {users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
          </select>
        </div>
        {["points","wins","losses","draws"].map((k) => (
          <div key={k}>
            <label className="label">{k}</label>
            <input type="number" className="input" value={form[k]} onChange={(e) => setForm({ ...form, [k]: parseInt(e.target.value || "0", 10) })} />
          </div>
        ))}
        <div className="col-span-full">
          <button className="btn-primary" type="submit"><Save className="w-4 h-4" /> Speichern</button>
        </div>
      </form>
      <div className="card-solid">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10 text-white/40">
              <th className="text-left p-3 font-mono text-[10px] uppercase tracking-[0.2em]">#</th>
              <th className="text-left p-3 font-mono text-[10px] uppercase tracking-[0.2em]">Spieler</th>
              <th className="text-right p-3 font-mono text-[10px] uppercase tracking-[0.2em]">Pts</th>
              <th className="text-right p-3 font-mono text-[10px] uppercase tracking-[0.2em]">W/L/D</th>
              <th className="text-right p-3 font-mono text-[10px] uppercase tracking-[0.2em]">Win%</th>
              <th className="text-right p-3 font-mono text-[10px] uppercase tracking-[0.2em]">#</th>
            </tr>
          </thead>
          <tbody>
            {items.map((e) => (
              <tr key={e.user_id} className="border-b border-white/5">
                <td className="p-3 font-mono">{e.rank}</td>
                <td className="p-3">{e.name}</td>
                <td className="p-3 text-right font-mono tabular-nums">{e.points}</td>
                <td className="p-3 text-right font-mono tabular-nums">{e.wins}/{e.losses}/{e.draws}</td>
                <td className="p-3 text-right font-mono tabular-nums">{e.win_rate}%</td>
                <td className="p-3 text-right">
                  <button onClick={() => remove(e.user_id, e.game)} className="btn-ghost !p-2 text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminChannels() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", description: "" });
  const load = async () => { const { data } = await api.get("/chat/channels"); setItems(data); };
  useEffect(() => { load(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    await api.post("/chat/channels", form);
    setForm({ name: "", description: "" }); load();
  };
  return (
    <div className="space-y-6 fade-in" data-testid="admin-channels-page">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gradient">Chat</div>
        <h1 className="font-display text-3xl tracking-tight mt-2">Kanäle</h1>
      </div>
      <form onSubmit={submit} className="card-solid p-5 grid grid-cols-1 md:grid-cols-3 gap-3">
        <div><label className="label">Name (kein #)</label><input required className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
        <div className="md:col-span-2"><label className="label">Beschreibung</label><input className="input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
        <div className="md:col-span-3"><button className="btn-primary" type="submit"><Plus className="w-4 h-4" /> Erstellen</button></div>
      </form>
      <div className="card-solid">
        <table className="w-full text-sm">
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-b border-white/5">
                <td className="p-3 font-mono">#{c.name}</td>
                <td className="p-3">{c.description}</td>
                <td className="p-3 font-mono text-xs text-white/40">{new Date(c.created_at).toLocaleDateString("de-DE")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminUsers() {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const empty = { name: "", email: "", password: "", role: "member" };
  const [form, setForm] = useState(empty);

  const load = async () => { const { data } = await api.get("/users"); setItems(data); };
  useEffect(() => { load(); }, []);

  const setRole = async (id, role) => {
    await api.put(`/admin/users/${id}/role`, null, { params: { role } });
    load();
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      setError("");
      await api.post("/admin/users", form);
      setForm(empty);
      setCreating(false);
      load();
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="admin-users-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gradient">Users</div>
          <h1 className="font-display text-3xl tracking-tight mt-2">Mitglieder</h1>
        </div>
        <button onClick={() => { setForm(empty); setCreating(true); setError(""); }} className="btn-primary" data-testid="create-user-btn">
          <Plus className="w-4 h-4" /> Benutzer hinzufügen
        </button>
      </div>

      {error && <div className="text-red-400 text-sm font-mono">{error}</div>}

      {creating && (
        <form onSubmit={submit} className="card-solid p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2 font-mono text-[10px] uppercase tracking-[0.3em] text-gradient">Neuer Benutzer</div>
          <div>
            <label className="label">Name</label>
            <input required type="text" className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <label className="label">E-Mail</label>
            <input required type="email" className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div>
            <label className="label">Passwort</label>
            <input required type="password" minLength={6} className="input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </div>
          <div>
            <label className="label">Rolle</label>
            <select className="input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div className="md:col-span-2 flex gap-2">
            <button type="submit" className="btn-primary"><Save className="w-4 h-4" /> Erstellen</button>
            <button type="button" onClick={() => setCreating(false)} className="btn-secondary">Abbrechen</button>
          </div>
        </form>
      )}

      <div className="card-solid">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-white/10 text-white/40">
            <th className="text-left p-3 font-mono text-[10px] uppercase tracking-[0.2em]">Name</th>
            <th className="text-left p-3 font-mono text-[10px] uppercase tracking-[0.2em]">Email</th>
            <th className="text-left p-3 font-mono text-[10px] uppercase tracking-[0.2em]">Member ID</th>
            <th className="text-left p-3 font-mono text-[10px] uppercase tracking-[0.2em]">Auth</th>
            <th className="text-left p-3 font-mono text-[10px] uppercase tracking-[0.2em]">Role</th>
            <th className="text-right p-3 font-mono text-[10px] uppercase tracking-[0.2em]">Aktion</th>
          </tr></thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.user_id} className="border-b border-white/5">
                <td className="p-3">{u.name}</td>
                <td className="p-3 text-white/70">{u.email}</td>
                <td className="p-3 font-mono text-xs">{u.member_id}</td>
                <td className="p-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">{u.auth_provider}</td>
                <td className="p-3 font-mono text-xs uppercase tracking-[0.2em]">{u.role}</td>
                <td className="p-3 text-right">
                  {u.role === "admin"
                    ? <button onClick={() => setRole(u.user_id, "member")} className="btn-ghost text-xs">→ Member</button>
                    : <button onClick={() => setRole(u.user_id, "admin")} className="btn-ghost text-xs text-primary">→ Admin</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
